import React from 'react';
import { useThemeMode } from '../../ThemeContext';
import { 
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

// Generic Alert Dialog Component
const AlertDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm = null, // Optional - if not provided, shows single "OK" button
  type = 'info', // 'warning', 'error', 'info', 'success', 'delete'
  title = "Alert",
  message = "This is an alert message.",
  itemName = "", // For delete confirmations
  confirmText = "OK",
  cancelText = "Cancel",
  icon = null, // Custom icon component or path
  iconColor = null, // Custom icon color
  mode = null, // Override theme mode if needed
  showCancelButton = true, // Whether to show cancel button (default true for delete, false for others)
  confirmButtonColor = null, // Custom confirm button color
  size = 'medium', // 'small', 'medium', 'large'
  children = null // Custom content instead of message
}) => {
  const { mode: themeMode } = useThemeMode();
  const currentMode = mode || themeMode;

  if (!isOpen) return null;

  // Default configurations based on type
  const getDefaultConfig = () => {
    switch (type) {
      case 'success':
        return {
          iconColor: '#26C3C0',
          defaultIcon: CheckCircleIcon,
          confirmButtonColor: '#26C3C0',
          showCancelButton: false
        };
      case 'warning':
        return {
          iconColor: '#ff9800',
          defaultIcon: WarningIcon,
          confirmButtonColor: '#ff9800',
          showCancelButton: true
        };
      case 'error':
        return {
          iconColor: '#f44336',
          defaultIcon: ErrorIcon,
          confirmButtonColor: '#f44336',
          showCancelButton: false
        };
      case 'delete':
        return {
          iconColor: '#f44336',
          defaultIcon: DeleteIcon,
          confirmButtonColor: '#f44336',
          showCancelButton: true
        };
      case 'info':
      default:
        return {
          iconColor: '#26C3C0',
          defaultIcon: InfoIcon,
          confirmButtonColor: '#26C3C0',
          showCancelButton: false
        };
    }
  };

  const config = getDefaultConfig();
  const IconComponent = icon || config.defaultIcon;
  const finalIconColor = iconColor || config.iconColor;
  const finalConfirmButtonColor = confirmButtonColor || config.confirmButtonColor;
  const shouldShowCancelButton = showCancelButton !== undefined ? showCancelButton : config.showCancelButton;

  // Size configurations
  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return { width: '320px', padding: '20px' };
      case 'large':
        return { width: '500px', padding: '32px' };
      case 'medium':
      default:
        return { width: '400px', padding: '24px' };
    }
  };

  const sizeConfig = getSizeConfig();

  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: sizeConfig.width,
        backgroundColor: currentMode === 'dark' ? '#1a1a1a' : '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        zIndex: 2001,
        padding: sizeConfig.padding
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: currentMode === 'dark' ? '#2a2a2a' : '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '16px'
          }}>
            {icon ? (
              typeof icon === 'string' ? (
                <img 
                  src={icon} 
                  alt={type} 
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    filter: `brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)` 
                  }} 
                />
              ) : (
                <div style={{ color: finalIconColor, fontSize: '20px' }}>
                  {icon}
                </div>
              )
            ) : (
              <IconComponent 
                style={{ 
                  color: finalIconColor, 
                  fontSize: '20px' 
                }} 
              />
            )}
          </div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: currentMode === 'dark' ? '#FFFFFF' : '#222222',
            margin: 0,
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {title}
          </h3>
        </div>

        {/* Content */}
        {children ? (
          <div style={{ marginBottom: '24px' }}>
            {children}
          </div>
        ) : (
          <p style={{
            fontSize: '14px',
            color: currentMode === 'dark' ? '#FFFFFF' : '#222222',
            margin: '0 0 24px 0',
            lineHeight: '1.5',
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {message}
            {itemName && (
              <strong style={{ color: currentMode === 'dark' ? '#FFFFFF' : '#222222' }}>
                "{itemName}"
              </strong>
            )}
          </p>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: shouldShowCancelButton ? 'flex-end' : 'center'
        }}>
          {shouldShowCancelButton && (
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                border: `1px solid ${currentMode === 'dark' ? '#444' : '#ddd'}`,
                borderRadius: '6px',
                backgroundColor: 'transparent',
                color: currentMode === 'dark' ? '#FFFFFF' : '#222222',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Urbanist, Arial, sans-serif',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = currentMode === 'dark' ? '#333' : '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm || onClose}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: finalConfirmButtonColor,
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Urbanist, Arial, sans-serif',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = finalConfirmButtonColor === '#f44336' ? '#d32f2f' : 
                                               finalConfirmButtonColor === '#4caf50' ? '#388e3c' :
                                               finalConfirmButtonColor === '#ff9800' ? '#f57c00' :
                                               finalConfirmButtonColor === '#26C3C0' ? '#009391' : '#d32f2f';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = finalConfirmButtonColor;
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </>
  );
};

export default AlertDialog; 